import axios from 'axios'
import { config } from '@/config/environment'

/**
 * ⚠️ ADMIN API CLIENT - CRITICAL SECURITY COMPONENT ⚠️
 *
 * This API client is EXCLUSIVELY for admin-only endpoints that require
 * cross-organization visibility (monitoring dashboard).
 *
 * KEY SECURITY FEATURES:
 * - Does NOT add X-Organization-Slug header
 * - Only adds Authorization header (JWT token)
 * - Used ONLY by monitoring service
 *
 * ⚠️ SECURITY WARNING:
 * DO NOT use this client for customer-facing features!
 * Customer pages MUST use the regular 'api' client from './api.ts'
 * which properly scopes requests to the current organization.
 *
 * Mixing these clients risks cross-organization data leaks and
 * violates security compliance requirements.
 *
 * APPROVED USAGE:
 * - src/services/monitoring.ts ✅
 * - Admin-only monitoring dashboard components ✅
 *
 * FORBIDDEN USAGE:
 * - src/services/integrations.ts ❌
 * - src/services/reports.ts ❌
 * - Any customer-facing service ❌
 *
 * @see docs/MONITORING_DASHBOARD_ARCHITECTURE.md Section 5.5
 */
export const adminApi = axios.create({
  baseURL: config.apiBaseUrl
})

// Add ONLY auth token (deliberately NO organization headers)
adminApi.interceptors.request.use(
  (config) => {
    const authToken = localStorage.getItem('authToken')

    config.headers = config.headers || {}

    // Add authentication token
    if (authToken) {
      if (!authToken.startsWith('Bearer ')) {
        config.headers.Authorization = `Bearer ${authToken}`
      } else {
        config.headers.Authorization = authToken
      }
    }

    // ✅ DELIBERATELY NOT adding X-Organization-Slug header
    // This client is for admin endpoints that need cross-org visibility

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Handle response errors (same pattern as regular api)
adminApi.interceptors.response.use(
  (response) => {
    return response
  },
  (error) => {
    if (error.response?.status === 401) {
      console.log('401 Unauthorized error received for admin API:', error.config?.url)

      // Don't auto-refresh in popup windows to preserve OAuth flow
      const isPopup = window.opener !== null
      if (isPopup) {
        console.log('OAuth popup detected - skipping automatic session handling')
        return Promise.reject(error)
      }

      // In main window, emit custom event for components to handle
      window.dispatchEvent(new CustomEvent('auth-error', {
        detail: {
          status: 401,
          url: error.config?.url
        }
      }))

      console.log('Authentication error detected for admin API, manual intervention required')
    }

    // Log 403 Forbidden errors (likely non-admin user trying to access admin endpoint)
    if (error.response?.status === 403) {
      console.error('403 Forbidden - Admin access required:', error.config?.url)
      console.error('User may not have admin role or backend validation failed')
    }

    return Promise.reject(error)
  }
)
